'use client'

import { useState, useRef, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { LoaderIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { WizardContainer, WizardStep } from '@/components/ui/wizard-container'
import { SourceTypeStep, parseAndValidateUrls } from './steps/SourceTypeStep'
import { NotebooksStep } from './steps/NotebooksStep'
import { ProcessingStep } from './steps/ProcessingStep'
import { useNotebooks } from '@/lib/hooks/use-notebooks'
// Skill-based execution replaces old transformation system
// useTransformations removed — default-apply skills run automatically on source upload
import { useCreateSource } from '@/lib/hooks/use-sources'
import { useSettings } from '@/lib/hooks/use-settings'
import { CreateSourceRequest } from '@/lib/types/api'
import { useI18n } from '@/lib/hooks/use-i18n'
import { useModelDefaults } from '@/lib/hooks/use-models'
import type { NotebookUploadedFile } from '@/lib/api/notebook-upload'

const MAX_BATCH_SIZE = 50

const createSourceSchema = (t: (key: string) => string) => z.object({
  type: z.enum(['link', 'upload', 'text']),
  title: z.string().optional(),
  url: z.string().optional(),
  content: z.string().optional(),
  file: z.any().optional(),
  notebooks: z.array(z.string()).optional(),
  skills: z.array(z.object({ skill_id: z.string(), model_id: z.string().optional(), locale: z.string().optional() })).optional(),
  embed: z.boolean(),
  build_graph: z.boolean(),
  async_processing: z.boolean(),
  enable_ocr: z.boolean(),
}).refine((data) => {
  if (data.type === 'link') {
    return !!data.url && data.url.trim() !== ''
  }
  if (data.type === 'text') {
    return !!data.content && data.content.trim() !== ''
  }
  // upload type validated via isStepValid (checks uploadedFiles state)
  return true
}, {
  message: t('addSourceDialog.validation.required'),
  path: ['type'],
}).refine((data) => {
  // Make title mandatory for text sources
  if (data.type === 'text') {
    return !!data.title && data.title.trim() !== ''
  }
  return true
}, {
  message: t('addSourceDialog.validation.titleRequired'),
  path: ['title'],
})

type CreateSourceFormData = z.infer<ReturnType<typeof createSourceSchema>>

interface AddSourceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultNotebookId?: string
}

interface ProcessingState {
  message: string
  progress?: number
}

interface BatchProgress {
  total: number
  completed: number
  failed: number
  currentItem?: string
}

export function AddSourceDialog({
  open,
  onOpenChange,
  defaultNotebookId
}: AddSourceDialogProps) {
  const { t, language } = useI18n()

  const WIZARD_STEPS: readonly WizardStep[] = useMemo(() => [
    { number: 1, title: t('addSourceDialog.step1.title'), description: t('addSourceDialog.step1.description') },
    { number: 2, title: t('addSourceDialog.step2.title'), description: t('addSourceDialog.step2.description') },
    { number: 3, title: t('addSourceDialog.step3.title'), description: t('addSourceDialog.step3.description') },
  ], [t])

  // Simplified state management
  const [currentStep, setCurrentStep] = useState(1)
  const [processing, setProcessing] = useState(false)
  const [processingStatus, setProcessingStatus] = useState<ProcessingState | null>(null)
  const [selectedNotebooks, setSelectedNotebooks] = useState<string[]>(
    defaultNotebookId ? [defaultNotebookId] : []
  )
  // Skills replace transformations — handled automatically by backend default-apply

  // Batch-specific state
  const [urlValidationErrors, setUrlValidationErrors] = useState<{ url: string; line: number }[]>([])
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null)
  // Pre-uploaded files (upload tab — files are uploaded before source creation)
  const [uploadedFiles, setUploadedFiles] = useState<NotebookUploadedFile[]>([])

  // Cleanup timeouts to prevent memory leaks
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // API hooks
  const createSource = useCreateSource()
  const { data: notebooks = [], isLoading: notebooksLoading } = useNotebooks()
  // Skills replace transformations — default-apply skills run automatically
  const { data: settings } = useSettings()
  const { data: modelDefaults } = useModelDefaults()
  const hasEmbeddingModel = !!modelDefaults?.default_embedding_model
  // transformation model check no longer needed — skills use their own model config

  const schema = useMemo(() => createSourceSchema(t), [t])

  // Form setup
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm<CreateSourceFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      notebooks: defaultNotebookId ? [defaultNotebookId] : [],
      embed: settings?.default_embedding_option === 'always' || settings?.default_embedding_option === 'ask',
      build_graph: false,
      async_processing: true,
      enable_ocr: false,
      skills: [],
    },
  })

  // Initialize form with defaults
  useEffect(() => {
    if (settings && defaultNotebookId && !notebooks?.length) {
      setSelectedNotebooks([defaultNotebookId])
      reset({
        type: 'link',
        notebooks: [defaultNotebookId],
        skills: [],
        embed: false,
        build_graph: false,
        async_processing: false,
        enable_ocr: false,
      })
    }
  }, [settings, defaultNotebookId, reset])

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const selectedType = watch('type')
  const watchedUrl = watch('url')
  const watchedContent = watch('content')
  const watchedTitle = watch('title')

  // Batch mode detection
  const { isBatchMode, itemCount, parsedUrls } = useMemo(() => {
    let urlCount = 0
    let fileCount = 0
    let parsedUrls: string[] = []

    if (selectedType === 'link' && watchedUrl) {
      const { valid } = parseAndValidateUrls(watchedUrl)
      parsedUrls = valid
      urlCount = valid.length
    }

    if (selectedType === 'upload') {
      fileCount = uploadedFiles.length
    }

    const isBatchMode = urlCount > 1 || fileCount > 1
    const itemCount = selectedType === 'link' ? urlCount : fileCount

    return { isBatchMode, itemCount, parsedUrls }
  }, [selectedType, watchedUrl, uploadedFiles])

  // Check for batch size limit
  const isOverLimit = itemCount > MAX_BATCH_SIZE

  // Step validation - now reactive with watched values
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!selectedType) return false
        // Check batch size limit
        if (isOverLimit) return false
        // Check for URL validation errors
        if (urlValidationErrors.length > 0) return false

        if (selectedType === 'link') {
          // In batch mode, check that we have at least one valid URL
          if (isBatchMode) {
            return parsedUrls.length > 0
          }
          return !!watchedUrl && watchedUrl.trim() !== ''
        }
        if (selectedType === 'text') {
          return !!watchedContent && watchedContent.trim() !== '' &&
            !!watchedTitle && watchedTitle.trim() !== ''
        }
        if (selectedType === 'upload') {
          return uploadedFiles.length > 0 && uploadedFiles.length <= MAX_BATCH_SIZE
        }
        return true
      case 2:
      case 3:
        return true
      default:
        return false
    }
  }

  // Navigation
  const handleNextStep = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()

    // Validate URLs when leaving step 1 in link mode
    if (currentStep === 1 && selectedType === 'link' && watchedUrl) {
      const { invalid } = parseAndValidateUrls(watchedUrl)
      if (invalid.length > 0) {
        setUrlValidationErrors(invalid)
        return
      }
      setUrlValidationErrors([])
    }

    if (currentStep < 3 && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1)
    }
  }

  // Clear URL validation errors when user edits
  const handleClearUrlErrors = () => {
    setUrlValidationErrors([])
  }

  const handlePrevStep = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleStepClick = (step: number) => {
    if (step <= currentStep || (step === currentStep + 1 && isStepValid(currentStep))) {
      setCurrentStep(step)
    }
  }

  // Selection handlers
  const handleNotebookToggle = (notebookId: string) => {
    const updated = selectedNotebooks.includes(notebookId)
      ? selectedNotebooks.filter(id => id !== notebookId)
      : [...selectedNotebooks, notebookId]
    setSelectedNotebooks(updated)
  }

  // handleTransformationToggle removed — skills applied automatically by backend

  // Single source submission
  const submitSingleSource = async (data: CreateSourceFormData): Promise<void> => {
    const preferredLanguage = language === 'zh' ? 'zh' : 'en'
    const createRequest: CreateSourceRequest = {
      type: data.type,
      notebooks: selectedNotebooks,
      url: data.type === 'link' ? data.url : undefined,
      content: data.type === 'text' ? data.content : undefined,
      title: data.title,
      skills: [],
      language: preferredLanguage,
      embed: data.embed,
      build_graph: data.build_graph,
      delete_source: false,
      async_processing: true,
      enable_ocr: data.enable_ocr,
    }

    if (data.type === 'upload' && uploadedFiles.length > 0) {
      const uf = uploadedFiles[0]
      createRequest.file_path = uf.fileUrl
      createRequest.file_url = uf.url
      createRequest.title = data.title || uf.name
    }

    await createSource.mutateAsync(createRequest)
  }

  // Batch submission
  const submitBatch = async (data: CreateSourceFormData): Promise<{ success: number; failed: number }> => {
    const results = { success: 0, failed: 0 }
    const items: { type: 'url' | 'file'; value: string; file_path?: string; file_url?: string }[] = []
    const preferredLanguage = language === 'zh' ? 'zh' : 'en'

    // Collect items to process
    if (data.type === 'link' && parsedUrls.length > 0) {
      parsedUrls.forEach(url => items.push({ type: 'url', value: url }))
    } else if (data.type === 'upload' && uploadedFiles.length > 0) {
      uploadedFiles.forEach(uf => items.push({ type: 'file', value: uf.name, file_path: uf.fileUrl, file_url: uf.url }))
    }

    setBatchProgress({
      total: items.length,
      completed: 0,
      failed: 0,
    })

    // Process each item sequentially
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const itemLabel = item.type === 'url'
        ? (item.value as string).substring(0, 50) + '...'
        : item.value

      setBatchProgress(prev => prev ? {
        ...prev,
        currentItem: itemLabel,
      } : null)

      try {
        const createRequest: CreateSourceRequest = {
          type: item.type === 'url' ? 'link' : 'upload',
          notebooks: selectedNotebooks,
          url: item.type === 'url' ? item.value as string : undefined,
          file_path: item.file_path,
          file_url: item.file_url,
          title: item.type === 'file' ? item.value : undefined,
          skills: [],
          language: preferredLanguage,
          embed: data.embed,
          build_graph: data.build_graph,
          delete_source: false,
          async_processing: true,
          enable_ocr: data.enable_ocr,
        }

        await createSource.mutateAsync(createRequest)
        results.success++
      } catch (error) {
        console.error(`Error creating source for ${itemLabel}:`, error)
        results.failed++
      }

      setBatchProgress(prev => prev ? {
        ...prev,
        completed: results.success,
        failed: results.failed,
      } : null)
    }

    return results
  }

  // Form submission
  const onSubmit = async (data: CreateSourceFormData) => {
    try {
      if ((data.embed || data.build_graph) && !hasEmbeddingModel) {
        toast.error(t('search.ask.error.noEmbeddingModel'))
        return
      }
      if (data.enable_ocr && !modelDefaults?.default_ocr_model) {
        toast.error(t('addSourceDialog.error.noOcrModel'))
        return
      }

      setProcessing(true)

      if (isBatchMode) {
        // Batch submission
        setProcessingStatus({ message: t('addSourceDialog.processing.batchMessage', { itemCount }) })
        const results = await submitBatch(data)

        // Show summary toast
        if (results.failed === 0) {
          toast.success(t('addSourceDialog.processing.batchSuccess', { successCount: results.success }))
        } else if (results.success === 0) {
          toast.error(t('addSourceDialog.processing.batchFailed', { failedCount: results.failed }))
        } else {
          toast.warning(t('addSourceDialog.processing.batchPartial', { successCount: results.success, failedCount: results.failed }))
        }

        handleClose()
      } else {
        // Single source submission
        setProcessingStatus({ message: t('addSourceDialog.processing.singleMessage') })
        await submitSingleSource(data)
        handleClose()
      }
    } catch (error) {
      console.error('Error creating source:', error)
      setProcessingStatus({
        message: t('addSourceDialog.processing.errorMessage'),
      })
      timeoutRef.current = setTimeout(() => {
        setProcessing(false)
        setProcessingStatus(null)
        setBatchProgress(null)
      }, 3000)
    }
  }

  // Dialog management
  const handleClose = () => {
    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    reset()
    setCurrentStep(1)
    setProcessing(false)
    setProcessingStatus(null)
    setSelectedNotebooks(defaultNotebookId ? [defaultNotebookId] : [])
    setUploadedFiles([])
    setUrlValidationErrors([])
    setBatchProgress(null)

    // Reset — skills run automatically
  }

  // Processing view
  if (processing) {
    const progressPercent = batchProgress
      ? Math.round(((batchProgress.completed + batchProgress.failed) / batchProgress.total) * 100)
      : undefined

    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]" showCloseButton={true}>
          <DialogHeader>
            <DialogTitle>
              {batchProgress ? t('addSourceDialog.processing.batchTitle') : t('addSourceDialog.processing.singleTitle')}
            </DialogTitle>
            <DialogDescription>
              {batchProgress
                ? t('addSourceDialog.processing.batchDescription', { total: batchProgress.total })
                : t('addSourceDialog.processing.singleDescription')
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3">
              <LoaderIcon className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">
                {processingStatus?.message || t('addSourceDialog.processing.label')}
              </span>
            </div>

            {/* Batch progress */}
            {batchProgress && (
              <>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-green-600">
                      <CheckCircleIcon className="h-4 w-4" />
                      {t('addSourceDialog.processing.completed', { completed: batchProgress.completed })}
                    </span>
                    {batchProgress.failed > 0 && (
                      <span className="flex items-center gap-1.5 text-destructive">
                        <XCircleIcon className="h-4 w-4" />
                        {t('addSourceDialog.processing.failed', { failed: batchProgress.failed })}
                      </span>
                    )}
                  </div>
                  <span className="text-muted-foreground">
                    {batchProgress.completed + batchProgress.failed} / {batchProgress.total}
                  </span>
                </div>

                {batchProgress.currentItem && (
                  <p className="text-xs text-muted-foreground truncate">
                    {t('addSourceDialog.processing.currentItem', { currentItem: batchProgress.currentItem })}
                  </p>
                )}
              </>
            )}

            {/* Single source progress */}
            {!batchProgress && processingStatus?.progress && (
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${processingStatus.progress}%` }}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  const currentStepValid = isStepValid(currentStep)

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] p-0">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>{t('addSourceDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('addSourceDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <WizardContainer
            currentStep={currentStep}
            steps={WIZARD_STEPS}
            onStepClick={handleStepClick}
            className="border-0"
          >
            {currentStep === 1 && (
              <SourceTypeStep
                // @ts-expect-error - Type inference issue with zod schema
                control={control}
                register={register}
                // @ts-expect-error - Type inference issue with zod schema
                errors={errors}
                urlValidationErrors={urlValidationErrors}
                onClearUrlErrors={handleClearUrlErrors}
                onFilesUploaded={setUploadedFiles}
                uploadedFileCount={uploadedFiles.length}
              />
            )}

            {currentStep === 2 && (
              <NotebooksStep
                notebooks={notebooks}
                selectedNotebooks={selectedNotebooks}
                onToggleNotebook={handleNotebookToggle}
                loading={notebooksLoading}
              />
            )}

            {currentStep === 3 && (
              <ProcessingStep
                control={control}
                settings={settings}
              />
            )}
          </WizardContainer>

          {/* Navigation */}
          <div className="flex justify-between items-center px-6 py-4 border-t border-border bg-muted">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              {t('addSourceDialog.actions.cancel')}
            </Button>

            <div className="flex gap-2">
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevStep}
                >
                  {t('addSourceDialog.actions.back')}
                </Button>
              )}

              {/* Show Next button on steps 1 and 2, styled as outline/secondary */}
              {currentStep < 3 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={(e) => handleNextStep(e)}
                  disabled={!currentStepValid}
                >
                  {t('addSourceDialog.actions.next')}
                </Button>
              )}

              {/* Show Done button on all steps, styled as primary */}
              <Button
                type="submit"
                disabled={!currentStepValid || createSource.isPending}
                className="min-w-[120px]"
              >
                {createSource.isPending ? t('addSourceDialog.actions.creating') : t('addSourceDialog.actions.done')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
